import React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { addSocSample, getSocSamples } from "services";

import { formatDayDate } from "utils";

import { ADD_SOC_SAMPLE, DEFAULT_CACHE_TIME, GET_SOC_SAMPLES } from "consts";

import { AdminDescriptions, Button, Col, ErrorBoundary, Form, InputNumber, Row, SkeletonWrapper, Message as message } from "components";

export const FarmFieldSocSamples = ({ fieldId, className = "" }: { fieldId: string; className?: string }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: socSamples, isLoading } = useQuery({
    queryKey: [GET_SOC_SAMPLES, fieldId],
    queryFn: () => getSocSamples({ fieldId }),
    gcTime: DEFAULT_CACHE_TIME,
    staleTime: DEFAULT_CACHE_TIME,
  });

  const { mutate, isPending } = useMutation({
    mutationKey: [ADD_SOC_SAMPLE],
    mutationFn: addSocSample,
    onSuccess: () => {
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: [GET_SOC_SAMPLES, fieldId] });
      message({ content: "Soc sample added successfully", icon: "gremlin" });
    },
  });

  const handleFinish = ({ soc }: { soc: number }) => {
    mutate({ fieldId, soc });
  };

  return (
    <ErrorBoundary>
      <div className={className}>
        <Row>
          <Col>
            <h3>Soc samples</h3>
          </Col>
        </Row>

        <Form onFinish={handleFinish} form={form}>
          <Row align="middle">
            <Col md={6}>
              <InputNumber name="soc" label="Soc sample value" rules={[{ required: true }]} min={0} />
            </Col>

            <Col md={4}>
              <Button dataTestId="save-crop-drawer" loading={isPending} type="primary" htmlType="submit">
                Add soc value
              </Button>
            </Col>
          </Row>
        </Form>

        <Row>
          <Col md={10}>
            <SkeletonWrapper isLoading={isLoading}>
              {socSamples?.length !== 0 && (
                <AdminDescriptions
                  columns={2}
                  items={socSamples?.flatMap(({ soc, createdAt }) => [
                    {
                      label: "Soc value",
                      children: soc,
                    },
                    {
                      label: "Created at",
                      children: formatDayDate(createdAt),
                    },
                  ])}
                />
              )}
            </SkeletonWrapper>
          </Col>
        </Row>
      </div>
    </ErrorBoundary>
  );
};
